import { useState } from "react";
import axios from "axios";
import Swal from "../../lib/appSwal";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getBizID } from "../../libs/todoSlice";
import { translateMessage } from "../errorKeyNote";
import {
  REGISTER_FIELD_KEYS,
  TITLE_EN_LIST,
  TITLE_TH_LIST,
  getRegisterError,
} from "./registerValidators";

const INITIAL_VALUES = {
  employeeId: "",
  titleTh: "",
  specialTitleTh: "",
  specialTitleEng: "",
  nameTh: "",
  surnameTh: "",
  nameEng: "",
  surnameEng: "",
  cid: "",
  tel: "",
  mobile: "",
  email: "",
  bizEmail: "",
  username: "",
  position: "",
  department: "",
  signatoryStatus: "",
  subOrganization: "",
  address: "",
};

// key ของ field ที่ backend ส่งกลับมาใน 400 -> key ของ state
const API_FIELD_TO_KEY = {
  username: "username",
  cid: "cid",
  name_th: "nameTh",
  surname_th: "surnameTh",
  name_eng: "nameEng",
  surname_eng: "surnameEng",
  tel: "tel",
  mobile: "mobile",
  email: "email",
};

// ใส่ key ลง payload เฉพาะตอนมีค่า (ไม่ส่ง "" / null)
const opt = (key, value) => (value && value.trim() !== "" ? { [key]: value } : {});

/**
 * @param prefix            prefix ที่ใช้ navigate หลัง submit / ตอน 401
 * @param requireStatus     true = บังคับเลือก signatoryStatus (หน้า PMUC)
 * @param resolveCaRequest  (values) => boolean  ถ้าส่งมา caRequest จะ derive จาก values (หน้า PMUC)
 *                          ถ้าไม่ส่ง caRequest จะมาจาก checkbox (toggleCa)
 */
export default function useRegisterForm({ prefix, requireStatus = false, resolveCaRequest, successAlert }) {
  const navigate = useNavigate();
  const business_id = useSelector(getBizID);

  const [values, setValues] = useState(INITIAL_VALUES);
  const [feedback, setFeedback] = useState({});
  const [caChecked, setCaChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const caRequest = resolveCaRequest ? !!resolveCaRequest(values) : caChecked;
  const rules = { caRequest, requireStatus };

  // ---------------- state helpers (functional update กัน stale closure) ----------------
  const setValue = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));
  const setError = (key, message) => setFeedback((prev) => ({ ...prev, [key]: message }));
  const clearErrors = (...keys) =>
    setFeedback((prev) => {
      const next = { ...prev };
      keys.forEach((k) => delete next[k]);
      return next;
    });

  // ---------------- validation ----------------
  // override = ค่าที่เพิ่งเลือก (ใช้กับ Select ที่ validate ก่อน state จะอัปเดต)
  const validateField = (key, override) => {
    const v = override === undefined ? values : { ...values, [key]: override };
    const message = getRegisterError(key, v, rules);
    setError(key, message);
    return !message;
  };

  const validateAll = () => {
    const next = {};
    let ok = true;
    REGISTER_FIELD_KEYS.forEach((key) => {
      const message = getRegisterError(key, values, rules);
      if (message) {
        next[key] = message;
        ok = false;
      }
    });
    setFeedback(next);
    return ok;
  };

  const isFormValid = REGISTER_FIELD_KEYS.every((key) => !getRegisterError(key, values, rules));

  // ---------------- CA helpers ----------------
  const resetCaFields = () => {
    setValues((prev) => ({ ...prev, subOrganization: "", address: "" }));
    clearErrors("subOrganization", "address", "bizEmail", "cid");
  };

  // ใช้กับ checkbox "ขอใบรับรองอิเล็กทรอนิกส์ (CA)" ของหน้าปกติ
  const toggleCa = (checked) => {
    setCaChecked(checked);
    if (!checked) {
      resetCaFields();
    } else if (!values.cid) {
      setError("cid", "กรุณากรอกเลขประจำตัวประชาชน"); // แจ้งทันทีตอนติ๊ก CA
    }
  };

  // ใช้ spread ลง <RegisterField {...bind("nameTh", onlyThai)} />
  const bind = (key, sanitize) => ({
    id: key,
    value: values[key],
    onChange: (value) => setValue(key, sanitize ? sanitize(value) : value),
    feedback: feedback[key] || "",
    onValidate: validateField,
  });

  // ---------------- submit ----------------
  const showWarning = (text) =>
    Swal.fire({
      title: "ข้อมูลไม่ถูกต้อง",
      icon: "warning",
      text: text || "กรุณาตรวจสอบข้อมูลที่กรอกอีกครั้ง",
    });

  const handleRegisterError = (error) => {
    const response = error?.response;
    const status = response?.status;
    const statusText = response?.statusText;
    const rawMessage = response?.data?.message;

    if (status === 401) {
      navigate(`/${prefix}`);
    } else if (status === 404) {
      Swal.fire({
        title: "ไม่พบข้อมูล business",
        icon: "error",
        text: "ไม่พบ business นี้ในระบบ กรุณาตรวจสอบลิงก์ลงทะเบียนอีกครั้ง",
      });
    } else if (status === 400) {
      const translated = translateMessage(rawMessage); // string / array / object

      if (translated && typeof translated === "object" && !Array.isArray(translated)) {
        const fieldErrors = {};
        const unmatched = [];

        Object.entries(translated).forEach(([field, errors]) => {
          const text = Array.isArray(errors) ? errors[0] : errors;
          const key = API_FIELD_TO_KEY[field];
          if (key) fieldErrors[key] = text;
          else unmatched.push(text);
        });

        const matchedCount = Object.keys(fieldErrors).length;
        if (matchedCount > 0) setFeedback((prev) => ({ ...prev, ...fieldErrors }));

        if (unmatched.length > 0) {
          showWarning(unmatched.join(", "));
        } else if (matchedCount > 0) {
          showWarning("กรุณาตรวจสอบข้อมูลในช่องที่มีข้อความสีแดงกำกับอยู่ แล้วลองอีกครั้ง");
        }
      } else if (Array.isArray(translated)) {
        showWarning(translated.join(", "));
      } else {
        showWarning(translated);
      }
    } else {
      Swal.fire({
        title: "ลงทะเบียนไม่สำเร็จ",
        icon: "error",
        text: (typeof rawMessage === "string" ? rawMessage : "") || statusText,
      });
    }
  };

  const register = async (extraPayload) => {
    const idx = TITLE_TH_LIST.indexOf(values.titleTh);
    const titleEn = idx !== -1 ? TITLE_EN_LIST[idx] : "";

    setLoading(true);
    try {
      await axios({
        method: "POST",
        url: `${process.env.REACT_APP_BACKEND_URL}/api/v1/employee-register/pre-register`,
        data: {
          business_id,
          employee_id: values.employeeId,
          title_th: values.titleTh,
          title_eng: titleEn,
          name_th: values.nameTh,
          surname_th: values.surnameTh,
          name_eng: values.nameEng,
          surname_eng: values.surnameEng,
          id_card_type: "ID_CARD",
          mobile: values.mobile,
          email: values.email,
          ca_request: caRequest,
          ...extraPayload, // เช่น { role_pmuc } ของหน้า PMUC
          ...opt("special_title_th", values.specialTitleTh),
          ...opt("special_title_eng", values.specialTitleEng),
          ...opt("username", values.username),
          ...opt("cid", values.cid),
          ...opt("tel", values.tel),
          ...opt("biz_email", values.bizEmail),
          ...opt("position", values.position),
          ...opt("department", values.department),
          ...(caRequest && opt("sub_organization", values.subOrganization)),
          ...(caRequest && opt("address", values.address)),
        },
      });

      Swal.fire(
        successAlert ?? { title: "ลงทะเบียนสำเร็จ", icon: "success" },
      ).then(() => {
        navigate(`/${prefix}`);
      });
    } catch (error) {
      handleRegisterError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (extraPayload = {}) => {
    if (!validateAll()) {
      setSnackbarOpen(true);
      Swal.fire({
        title: "กรอกข้อมูลไม่ครบถ้วน",
        icon: "warning",
        text: "กรุณาตรวจสอบข้อมูลในช่องที่มีข้อความสีแดงกำกับอยู่ แล้วลองอีกครั้ง",
      });
      return;
    }
    await register(extraPayload);
  };

  return {
    values,
    feedback,
    setValue,
    bind,
    validateField,
    isFormValid,
    caRequest,
    toggleCa,
    resetCaFields,
    loading,
    snackbarOpen,
    closeSnackbar: () => setSnackbarOpen(false),
    handleSubmit,
  };
}